import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  onSnapshot, 
  setDoc 
} from 'firebase/firestore';
import { db } from '../../../firebase';

// Create the context
const GolfScoringContext = createContext();

export const GolfScoringProvider = ({ children, activityId }) => {
  const [gameSetup, setGameSetup] = useState({
    mode: null,
    holeCount: null,
    teams: [],
    status: 'not_started' // 'not_started', 'in_progress', 'completed'
  });

  // Setup the golf game
  const setupGolfGame = async (holeCount, teams) => {
    if (!activityId) {
      console.error('No activityId provided');
      return;
    }

    const golfScoringRef = doc(db, 'activities', activityId, 'golfScoring', 'setup');

    try {
      // Validate inputs
      if (![9, 18].includes(holeCount)) {
        throw new Error('Invalid hole count. Must be 9 or 18.');
      }

      if (!Array.isArray(teams) || teams.length === 0) {
        throw new Error('Invalid teams data. Must be a non-empty array.');
      }

      // Prepare teams with structure
      const formattedTeams = teams.map((team, index) => {
        if (!team.name || !Array.isArray(team.scoreAdmins)) {
          throw new Error(`Invalid team data for team at index ${index}`);
        }

        return {
          id: team.id || `team-${Date.now()}-${index}`,
          name: team.name,
          scoreAdmins: team.scoreAdmins,
          holes: Array(holeCount).fill(0),
          totalScore: 0
        };
      });

      // Create/update the golf scoring document
      const gameSetupData = {
        holeCount,
        teams: formattedTeams,
        status: 'in_progress',
        createdAt: new Date().toISOString()
      };

      // Log the data being sent to Firestore
      console.log('Golf game setup data:', JSON.stringify(gameSetupData, null, 2));

      await setDoc(golfScoringRef, gameSetupData);

      console.log('Golf game setup completed');
    } catch (error) {
      console.error('Error setting up golf game:', error);
      throw error;
    }
  };

  // Update team score
  const updateTeamScore = async (teamId, holeIndex, score) => {
    if (!activityId) {
      console.error('No activityId provided');
      return;
    }

    const golfScoringRef = doc(db, 'activities', activityId, 'golfScoring', 'setup');

    try {
      // Get current game setup
      const currentSetup = await getDoc(golfScoringRef);
      const data = currentSetup.data();

      if (!data) {
        throw new Error('No game setup found');
      }

      // Create a copy of teams
      const updatedTeams = JSON.parse(JSON.stringify(data.teams));

      // Find the team
      const teamIndex = updatedTeams.findIndex(team => team.id === teamId);
      if (teamIndex === -1) {
        throw new Error('Team not found');
      }

      // Update hole score
      updatedTeams[teamIndex].holes[holeIndex] = Math.max(0, score);

      // Recalculate total score
      updatedTeams[teamIndex].totalScore = updatedTeams[teamIndex].holes.reduce((a, b) => a + b, 0);

      const updateData = { 
        teams: updatedTeams,
        updatedAt: new Date().toISOString()
      };

      // Log the data being updated in Firestore
      console.log('Updating team score:', JSON.stringify(updateData, null, 2));

      // Update Firestore
      await updateDoc(golfScoringRef, updateData);

      console.log('Team score updated successfully');
    } catch (error) {
      console.error('Error updating team score:', error);
      throw error;
    }
  };

  // Real-time listener for game setup
  useEffect(() => {
    if (!activityId) {
      console.error('No activityId provided for real-time listener');
      return;
    }

    const golfScoringRef = doc(db, 'activities', activityId, 'golfScoring', 'setup');

    const unsubscribe = onSnapshot(golfScoringRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newGameSetup = {
          holeCount: data.holeCount || null,
          teams: data.teams || [],
          status: data.status || 'not_started'
        };
        console.log('Real-time update received:', JSON.stringify(newGameSetup, null, 2));
        setGameSetup(newGameSetup);
      } else {
        console.log('No golf scoring setup found');
      }
    }, (error) => {
      console.error('Error listening to golf scoring setup:', error);
    });

    return () => unsubscribe();
  }, [activityId]);

  return (
    <GolfScoringContext.Provider value={{
      gameSetup,
      setupGolfGame,
      updateTeamScore
    }}>
      {children}
    </GolfScoringContext.Provider>
  );
};

export const useGolfScoring = () => {
  const context = useContext(GolfScoringContext);
  if (context === undefined) {
    throw new Error('useGolfScoring must be used within a GolfScoringProvider');
  }
  return context;
};